import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Music2,
  Bell,
  UserCircle,
  LogOut,
  Menu,
  X,
  Music,
  Library,
  BarChart3,
  Settings,
  CreditCard,
  HelpCircle,
  ChevronRight,
  ChevronsUpDown,
  Check,
  ShieldCheck,
  Plus,
  CalendarDays,
  Upload,
  Mail,
  FileText,
  MessageCircle,
  Receipt,
  Trophy,
  ShieldAlert,
  Building2,
  Wallet,
  Armchair,
  BellRing,
} from "lucide-react";
import { BandLogo } from "./MediaImages";
import BottomTabBar from "./BottomTabBar";
import { useStandaloneMode } from "../hooks/useStandaloneMode";
import { useAdminControls } from "../AdminMode";

const Layout = () => {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isStandalone = useStandaloneMode();

  // Apply a body class so the mobile bottom-tab-bar only reserves space when
  // running as an installed PWA. Browser-tab visitors keep the original
  // drawer-only layout (no tab bar, no extra bottom padding).
  useEffect(() => {
    if (isStandalone) {
      document.body.classList.add("has-bottom-tabs");
      return () => document.body.classList.remove("has-bottom-tabs");
    }
    document.body.classList.remove("has-bottom-tabs");
    return undefined;
  }, [isStandalone]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [bands, setBands] = useState([]);
  const [canManageInvoices, setCanManageInvoices] = useState(false);

  const isAdmin = user?.role === "admin";
  const isDep = !!user?.is_dep && !isAdmin;
  // iter-66k: info-only contacts (e.g. parents, supporters, past members on the
  // mailing list) — they see events + documents only and never RSVP.
  const isInfoOnly = !!user?.is_info_only && !isAdmin;
  // iter-56: when the user is still using their temp password from a welcome email,
  // force them onto /profile and block every other route until they set a new one.
  const mustChangePassword = !!user?.temp_password;
  const { adminMode, toggle: toggleAdminMode, showAdminControls } = useAdminControls();

  // iter-95: unread notification count for the bell-icon badge. Polls every
  // 60s and refreshes whenever the user navigates (so opening /notifications
  // immediately resets the badge when they read items).
  const [unreadCount, setUnreadCount] = useState(0);
  // iter-98: unread chat count for the Chat nav item badge.
  const [unreadChat, setUnreadChat] = useState(0);
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await apiClient.get("/notifications/unread-count");
        if (!cancelled) setUnreadCount(res.data?.count || 0);
      } catch {
        /* silent — badge just stays at its last value */
      }
      try {
        const r2 = await apiClient.get("/chat/unread-count");
        if (!cancelled) setUnreadChat(r2.data?.count || 0);
      } catch {
        /* silent */
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.id, location.pathname]);

  // Load memberships for the switcher
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await apiClient.get("/bands/my");
        setBands(res.data.bands || []);
      } catch {
        // silent
      }
    })();
  }, [user?.id, user?.current_band_id, user?.band_id]);

  // Treasurer/admin permission for the Invoices nav item
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await apiClient.get("/invoicing/permissions");
        setCanManageInvoices(!!r.data.can_manage_invoices);
      } catch {
        setCanManageInvoices(false);
      }
    })();
  }, [user?.id, user?.current_band_id, user?.band_id]);

  // One-off "Save as app" hint for mobile users on first login
  useEffect(() => {
    if (!user) return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;
    if (localStorage.getItem("vivo-install-hint-shown")) return;
    const t = setTimeout(() => {
      toast.info("Tip: install Vivo on your phone's home screen from Profile → Install as app", {
        duration: 8000,
      });
      localStorage.setItem("vivo-install-hint-shown", "1");
    }, 4000);
    return () => clearTimeout(t);
  }, [user?.id]);

  const mainNavigation = mustChangePassword
    ? []
    : isInfoOnly
    ? [
        { name: "Events", href: "/events", icon: Calendar },
        { name: "Documents", href: "/documents", icon: FileText },
      ]
    : isDep
    ? [
        { name: "Dashboard", href: "/", icon: LayoutDashboard },
        { name: "Events", href: "/events", icon: Calendar },
      ]
    : [
        { name: "Dashboard", href: "/", icon: LayoutDashboard },
        // iter-81: "All Calendar" only useful for users in 2+ bands. Hide
        // sidebar entry + bounce direct nav for single-band members.
        ...(bands.length > 1 ? [{ name: "All Calendar", href: "/calendar/all", icon: CalendarDays }] : []),
        { name: "Events", href: "/events", icon: Calendar },
        { name: "Players", href: "/players", icon: Users },
        // Sections — admin-only (org config; members don't need it)
        ...(isAdmin ? [{ name: "Sections", href: "/sections", icon: Music2 }] : []),
        { name: "Music", href: "/music", icon: Library },
        { name: "Chat", href: "/chat", icon: MessageCircle },
        { name: "Documents", href: "/documents", icon: FileText },
      ];

  const accountNavigation = mustChangePassword
    ? [{ name: "Profile", href: "/profile", icon: UserCircle }]
    : isInfoOnly
    ? [
        { name: "Profile", href: "/profile", icon: UserCircle },
        { name: "Help", href: "/help/player", icon: HelpCircle },
      ]
    : [
        { name: "Profile", href: "/profile", icon: UserCircle },
        { name: "Achievements", href: "/achievements", icon: Trophy },
        { name: "My Memberships", href: "/my-memberships", icon: Building2 },
        { name: "Help", href: showAdminControls ? "/help/admin" : "/help/player", icon: HelpCircle },
      ];

  const financeNavigation = [
    { name: "Finance Home", href: "/admin/finance", icon: Wallet },
    { name: "Invoices", href: "/invoices", icon: Receipt },
  ];

  const adminNavigation = [
    { name: "Admin Home", href: "/admin", icon: ShieldCheck },
    { name: "Notifications", href: "/notifications", icon: Bell },
    { name: "Reports", href: "/reports", icon: BarChart3 },
    { name: "Missing Players", href: "/missing-players", icon: ShieldAlert },
    { name: "Roster", href: "/admin/roster", icon: Armchair },
    { name: "Push Diagnostic", href: "/admin/push-diagnostic", icon: BellRing },
    { name: "Bulk Imports", href: "/admin/imports", icon: Upload },
    { name: "Monthly Digest", href: "/admin/digest", icon: Mail },
    { name: "Band Settings", href: "/settings", icon: Settings },
    { name: "Subscription", href: "/subscription", icon: CreditCard },
  ];

  // iter-56: temp-password gate — runs BEFORE the dep gate so a brand-new dep
  // who's still on a temp password lands on /profile rather than /dashboard.
  useEffect(() => {
    if (!mustChangePassword) return;
    if (location.pathname === "/profile") return;
    toast.warning("Please set your own password before continuing.");
    navigate("/profile", { replace: true });
  }, [mustChangePassword, location.pathname, navigate]);

  // iter-52: Deps are restricted to Dashboard + Events (filtered) + Profile/Help/MyData.
  // Any direct navigation to a disallowed path bounces back to Dashboard with a toast.
  const allowedExactForDeps = new Set(["/", "/profile", "/achievements", "/my-memberships", "/help/player", "/my-data"]);
  const allowedPrefixesForDeps = ["/events"]; // covers /events and /events/{id}
  const isPathAllowedForDep = (path) =>
    allowedExactForDeps.has(path) ||
    allowedPrefixesForDeps.some((p) => path === p || path.startsWith(`${p}/`));
  const depBlocked = isDep && !isPathAllowedForDep(location.pathname);
  useEffect(() => {
    if (depBlocked) {
      toast.info("Not available for deps");
      navigate("/", { replace: true });
    }
  }, [depBlocked, navigate]);

  // iter-66k: Info-only contacts are restricted to Events + Documents + Profile/Help/MyData.
  // Landing on "/" bounces them to /events since they don't have a dashboard view.
  const allowedExactForInfoOnly = new Set(["/profile", "/help/player", "/my-data"]);
  const allowedPrefixesForInfoOnly = ["/events", "/documents"];
  const isPathAllowedForInfoOnly = (path) =>
    allowedExactForInfoOnly.has(path) ||
    allowedPrefixesForInfoOnly.some((p) => path === p || path.startsWith(`${p}/`));
  const infoOnlyBlocked = isInfoOnly && !isPathAllowedForInfoOnly(location.pathname);
  useEffect(() => {
    if (infoOnlyBlocked) {
      // Silently bounce to /events — toast is noise for users who land here repeatedly
      navigate("/events", { replace: true });
    }
  }, [infoOnlyBlocked, navigate]);

  // Sections page is admin-only. Non-admin members (and any deeper paths under
  // /sections) get bounced to Dashboard with a toast.
  const sectionsBlocked =
    !isAdmin &&
    !isDep &&
    !isInfoOnly &&
    (location.pathname === "/sections" || location.pathname.startsWith("/sections/"));
  useEffect(() => {
    if (sectionsBlocked) {
      toast.info("Sections management is admin-only");
      navigate("/", { replace: true });
    }
  }, [sectionsBlocked, navigate]);

  // iter-81: "All Calendar" only makes sense for users with 2+ bands. Bounce
  // single-band members back to Dashboard if they hit /calendar/all directly.
  // We wait until bands has loaded (length > 0) before deciding so we don't
  // race the /bands/my fetch on first paint.
  const allCalendarBlocked =
    bands.length === 1 &&
    !isDep &&
    !isInfoOnly &&
    location.pathname === "/calendar/all";
  useEffect(() => {
    if (allCalendarBlocked) {
      navigate("/", { replace: true });
    }
  }, [allCalendarBlocked, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const showSubscriptionWarning =
    user?.subscription_status === "expired" ||
    (user?.subscription_status === "trial" && user?.days_remaining <= 7);

  const switchBand = async (band_id) => {
    if (band_id === user?.current_band_id) {
      setSwitcherOpen(false);
      return;
    }
    try {
      await apiClient.post("/bands/switch", { band_id });
      await refreshUser();
      toast.success("Switched band");
      setSwitcherOpen(false);
      // Reload to refresh all lists
      setTimeout(() => window.location.reload(), 250);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to switch band");
    }
  };

  const joinAdditional = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setJoining(true);
    try {
      const res = await apiClient.post("/bands/join-additional", { join_code: code });
      toast.success(res.data?.message || "Joined band");
      setJoinDialogOpen(false);
      setJoinCode("");
      await refreshUser();
      // Reload memberships
      const me = await apiClient.get("/bands/my");
      setBands(me.data.bands || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to join band");
    } finally {
      setJoining(false);
    }
  };

  const NavItem = ({ item, onClick }) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    return (
      <Link
        to={item.href}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={onClick}
        className={`
          relative flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 group
          ${active
            ? "bg-white/[0.05] text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
          }
        `}
      >
        {active && (
          <span
            aria-hidden
            className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r bg-primary"
          />
        )}
        <Icon className={`w-[18px] h-[18px] ${active ? "" : "group-hover:text-foreground"}`} />
        <span className="font-medium text-sm flex-1">{item.name}</span>
        {item.name === "Notifications" && unreadCount > 0 && (
          <span
            className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none"
            data-testid="sidebar-notifications-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
        {item.name === "Chat" && unreadChat > 0 && (
          <span
            className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none"
            data-testid="sidebar-chat-badge"
          >
            {unreadChat > 99 ? "99+" : unreadChat}
          </span>
        )}
        {item.name === "Subscription" && showSubscriptionWarning && (
          <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
        )}
        {active && <ChevronRight className="w-4 h-4 opacity-50" />}
      </Link>
    );
  };

  const currentBand = bands.find((b) => b.is_current) || {
    band_name: user?.band_name,
    role: user?.role,
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden max-w-[100vw]">
      {/* Mobile Header — condensed, safe-area aware, bell + menu on the right */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 glass border-b border-white/[0.06] z-50"
        style={{ paddingTop: "var(--safe-top, 0px)" }}
      >
        <div className="h-14 flex items-center justify-between px-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <BandLogo
              bandId={user?.band_id}
              className="w-8 h-8 rounded-lg flex-shrink-0"
              fallback={
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Music className="w-4 h-4 text-primary" />
                </div>
              }
            />
            <span className="font-heading font-bold text-[15px] truncate" data-testid="mobile-header-band-name">
              {currentBand?.band_name || "Vivo"}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/notifications")}
              data-testid="mobile-header-notifications"
              className="relative hover:bg-white/[0.04] h-9 w-9 text-foreground hover:text-foreground"
              aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span
                  className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none ring-2 ring-background"
                  data-testid="mobile-header-notifications-badge"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-btn"
              className="hover:bg-white/[0.04] h-9 w-9 text-foreground hover:text-foreground"
              aria-label={sidebarOpen ? "Close menu" : "Open menu"}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-[260px] z-40
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          bg-[hsl(240,6%,4%)] border-r border-white/[0.04]
        `}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center gap-3 px-5 border-b border-white/[0.04]">
            <BandLogo
              bandId={user?.band_id}
              className="w-10 h-10 rounded-xl"
              fallback={
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20">
                  <Music className="w-5 h-5 text-primary" />
                </div>
              }
            />
            <div>
              <h1 className="font-heading font-bold text-lg tracking-tight">{currentBand?.band_name || "Vivo"}</h1>
              <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">
                Musician Management
              </p>
            </div>
          </div>

          {/* Band Switcher */}
          <div className="px-4 py-3 border-b border-white/[0.04]">
            <DropdownMenu open={switcherOpen} onOpenChange={setSwitcherOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] transition"
                  data-testid="band-switcher-trigger"
                >
                  <div className="text-left min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">
                      Current Band
                    </p>
                    <p className="font-semibold text-sm text-primary truncate">
                      {currentBand?.band_name || "No band"}
                    </p>
                  </div>
                  <ChevronsUpDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[220px]" data-testid="band-switcher-menu">
                <DropdownMenuLabel>Your Bands</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {bands.length === 0 && (
                  <DropdownMenuItem disabled>No other bands</DropdownMenuItem>
                )}
                {bands.map((b) => (
                  <DropdownMenuItem
                    key={b.band_id}
                    onClick={() => switchBand(b.band_id)}
                    className="flex items-center justify-between gap-2"
                    data-testid={`band-switcher-option-${b.band_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{b.band_name}</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {b.role}
                      </p>
                    </div>
                    {b.is_current && <Check className="w-4 h-4 text-primary" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setSwitcherOpen(false);
                    setJoinDialogOpen(true);
                  }}
                  className="text-primary"
                  data-testid="join-another-band-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Join another band
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-6 px-3">
              {/* Main Nav - visible to everyone */}
              <div>
                <p className="section-label px-4 mb-2">Main</p>
                <div className="space-y-0.5">
                  {mainNavigation.map((item) => (
                    <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
                  ))}
                </div>
              </div>

              {/* Account Nav */}
              <div>
                <p className="section-label px-4 mb-2">Account</p>
                <div className="space-y-0.5">
                  {accountNavigation.map((item) => (
                    <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
                  ))}
                </div>
              </div>

              {/* Finance Nav - admins + treasurers */}
              {canManageInvoices && (
                <div data-testid="finance-nav-section">
                  <p className="section-label px-4 mb-2 flex items-center gap-2">
                    <Receipt className="w-3 h-3 text-primary" />
                    Treasurer Admin
                  </p>
                  <div className="space-y-0.5">
                    {financeNavigation.map((item) => (
                      <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Nav — only when admin mode is ON */}
              {showAdminControls && (
                <div data-testid="admin-nav-section">
                  <p className="section-label px-4 mb-2 flex items-center gap-2">
                    <ShieldCheck className="w-3 h-3 text-primary" />
                    Admin
                  </p>
                  <div className="space-y-0.5">
                    {adminNavigation.map((item) => (
                      <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
                    ))}
                  </div>
                </div>
              )}
            </nav>
          </ScrollArea>

          {/* User Info & Logout */}
          <div
            className="p-4 border-t border-white/[0.04] bg-black/20"
            style={
              isStandalone
                ? { paddingBottom: "calc(16px + var(--bottom-tab-height) + var(--safe-bottom))" }
                : undefined
            }
          >
            <div className="flex items-center gap-3 px-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center">
                <span className="text-sm font-semibold text-foreground">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {isAdmin ? (adminMode ? "Administrator · Admin Mode" : "Administrator · Member View") : "Member"}
                  {bands.length > 1 && ` · ${bands.length} bands`}
                </p>
              </div>
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={toggleAdminMode}
                className={`w-full mb-2 px-3 h-9 rounded-md text-xs font-medium flex items-center justify-between gap-2 transition-colors border ${
                  adminMode
                    ? "bg-primary/10 border-primary/30 text-primary hover:bg-primary/15"
                    : "bg-secondary/50 border-white/[0.06] text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
                }`}
                data-testid="admin-mode-toggle"
                aria-pressed={adminMode}
                title={adminMode ? "Hide admin controls — go back to the member view" : "Show admin controls across the app"}
              >
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Admin Mode
                </span>
                <span
                  className={`relative inline-flex items-center w-8 h-4 rounded-full transition-colors flex-shrink-0 ${
                    adminMode ? "bg-primary" : "bg-white/15"
                  }`}
                  aria-hidden="true"
                >
                  <span
                    className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                      adminMode ? "translate-x-4" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="w-full px-3 h-9 rounded-md text-xs font-medium flex items-center gap-2 transition-colors border bg-secondary/50 border-white/[0.06] text-muted-foreground hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400"
              data-testid="logout-btn"
              title="Sign out of Vivo"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Join another band dialog */}
      <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
        <DialogContent data-testid="join-another-band-dialog">
          <DialogHeader>
            <DialogTitle>Join another band</DialogTitle>
            <DialogDescription>
              Enter the invite code for the additional band you want to join. You&apos;ll stay a member
              of your current band and can switch between them at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="join-additional-code">Band invite code</Label>
            <Input
              id="join-additional-code"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="e.g. ABC123"
              className="font-mono tracking-widest text-center text-lg"
              maxLength={10}
              data-testid="join-additional-code-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setJoinDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={joinAdditional}
              disabled={joining || !joinCode.trim()}
              data-testid="confirm-join-additional-btn"
            >
              {joining ? "Joining..." : "Join band"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="lg:ml-[260px] min-h-screen pt-14 lg:pt-0 overflow-x-hidden">
        <ImpersonationBanner />
        <div className="p-5 lg:p-8 max-w-[1600px] min-w-0">
          {depBlocked || (mustChangePassword && location.pathname !== "/profile") ? (
            <div className="text-sm text-muted-foreground py-12 text-center" data-testid="dep-redirect-placeholder">
              Redirecting…
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </main>

      {/* Mobile bottom tab bar — ONLY shown when the app is running as an
          installed PWA (iOS home screen, Android launcher, or a TWA/wrapper
          generated via PWABuilder for the Play Store / App Store).
          Browser-tab visitors continue to use the sidebar drawer. */}
      {isStandalone && <BottomTabBar onMoreClick={() => setSidebarOpen(true)} isDep={isDep} />}
    </div>
  );
};

// iter-103 — sticky banner shown when the current session is a system-admin
// impersonation magic-link. Reads localStorage.impersonation (set by the
// /impersonate route + /login?impersonate_token=... handler in Login.jsx)
// and surfaces the target email + a one-click exit that restores the
// sys-admin's own session.
function ImpersonationBanner() {
  const raw = typeof window !== "undefined" ? localStorage.getItem("impersonation") : null;
  if (!raw) return null;
  let info;
  try { info = JSON.parse(raw); } catch { return null; }
  const expired = info.exp && info.exp * 1000 < Date.now();
  const targetEmail = info.target_email || info.email || "band admin";
  const exitImpersonation = () => {
    localStorage.removeItem("impersonation");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // iter-135: sys-admin console lives at /system-admin under its own
    // `systemAdminToken` storage key — send the user back there so their
    // sys-admin session picks up seamlessly.
    window.location.href = "/system-admin";
  };
  return (
    <div
      className="sticky top-0 z-40 bg-orange-500 text-white text-xs sm:text-sm px-3 py-2 flex items-center justify-between gap-3 border-b border-orange-700 shadow-md"
      data-testid="impersonation-banner"
    >
      <span className="truncate">
        <strong>You are impersonating {targetEmail}</strong>
        {expired ? " (token expired)" : ""} — all actions are audited.
      </span>
      <button
        type="button"
        onClick={exitImpersonation}
        className="px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 text-white text-xs font-medium shrink-0 whitespace-nowrap"
        data-testid="impersonation-exit-btn"
      >
        Click to exit
      </button>
    </div>
  );
}

export default Layout;
